
import React, { useState, useEffect, useRef } from 'react';
import { TeacherProfile, TeacherVideo, GSCBackingPackage } from '../../types';
import { getTeacherProfile, saveTeacherProfile, getTeacherVideos, saveTeacherVideo, deleteTeacherVideo } from '../../services/storageService';
import { blobToBase64, createGscFile } from '../../services/gscFormatService';

interface TeacherHubProps {
    onSelectVideo: (video: TeacherVideo) => void;
    lang: 'en' | 'fr';
}

export const TeacherHub: React.FC<TeacherHubProps> = ({ onSelectVideo, lang }) => {
    const [profile, setProfile] = useState<TeacherProfile>({ name: '', specialty: '', bio: '', avatarUrl: '' });
    const [videos, setVideos] = useState<TeacherVideo[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const load = async () => {
            const p = await getTeacherProfile();
            if (p) setProfile(p);
            const v = await getTeacherVideos();
            setVideos(v.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
        };
        load();
    }, []);

    const handleExportGsc = async (video: TeacherVideo) => {
        const base64 = await blobToBase64(video.videoBlob);
        const pkg: GSCBackingPackage = {
            format: "GSC_BACKING_TRACK",
            version: "1.0",
            metadata: {
                title: video.title,
                artist: profile.name || "Teacher",
                bpm: video.bpm,
                timeSignature: "4/4",
                grid: video.grid,
                difficulty: 4,
                tags: ["lesson", video.title.toLowerCase()]
            },
            audio: {
                backingBase64: base64,
                referenceBase64: base64,
                mimeType: video.videoBlob.type
            },
            coachingHints: {
                focusZones: [],
                commonPitfalls: video.notes ? [video.notes] : []
            }
        };

        const blob = createGscFile(pkg);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${video.title.replace(/\s+/g, '_')}.gsc`;
        a.click();
    };

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        await saveTeacherProfile(profile);
        setIsEditing(false);
    };

    const handleAddVideo = async (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const file = fileInputRef.current?.files?.[0];
        if (!file) return;

        const newVideo: TeacherVideo = {
            id: crypto.randomUUID(),
            title: formData.get('title') as string,
            bpm: parseInt(formData.get('bpm') as string) || 90,
            grid: formData.get('grid') as string,
            notes: formData.get('notes') as string,
            date: new Date().toISOString(),
            videoBlob: file,
            fileName: file.name
        };

        await saveTeacherVideo(newVideo);
        setVideos([newVideo, ...videos]);
        setIsAdding(false);
    };

    const handleDelete = async (id: string) => {
        if (confirm(lang === 'fr' ? "Supprimer cette vidéo ?" : "Delete this video?")) {
            await deleteTeacherVideo(id);
            setVideos(videos.filter(v => v.id !== id));
        }
    };

    return (
        <div className="space-y-12 animate-fade-in">
            <div className="bg-gradient-to-r from-emerald-900/40 to-black p-8 rounded-[48px] border border-emerald-500/20 shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 text-6xl font-black italic pointer-events-none uppercase">Mentor</div>
                <div className="flex flex-col md:flex-row items-center gap-10 relative z-10">
                    <div className="w-28 h-28 rounded-[36px] bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center text-5xl shadow-2xl">
                        {profile.avatarUrl ? <img src={profile.avatarUrl} className="w-full h-full object-cover rounded-[34px]" /> : "👨‍🏫"}
                    </div>
                    <div className="flex-1 text-center md:text-left space-y-2">
                        {isEditing ? (
                            <form onSubmit={handleSaveProfile} className="space-y-4">
                                <input type="text" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} placeholder="Nom du Professeur" className="bg-black/60 border border-white/10 rounded-xl px-4 py-2 text-white w-full max-w-sm outline-none focus:border-emerald-500" />
                                <input type="text" value={profile.specialty} onChange={e => setProfile({...profile, specialty: e.target.value})} placeholder="Spécialité (ex: Blues, Shred)" className="bg-black/60 border border-white/10 rounded-xl px-4 py-2 text-white w-full max-w-sm block outline-none focus:border-emerald-500" />
                                <div className="flex gap-2">
                                    <button type="submit" className="px-6 py-2 bg-emerald-500 text-black font-black uppercase text-[10px] rounded-lg">Valider</button>
                                    <button onClick={() => setIsEditing(false)} className="px-6 py-2 bg-white/5 text-white font-black uppercase text-[10px] rounded-lg">Annuler</button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <h3 className="text-4xl font-black text-white italic tracking-tighter uppercase">{profile.name || (lang === 'fr' ? "Mon Professeur" : "My Teacher")}</h3>
                                <div className="flex items-center gap-3 justify-center md:justify-start">
                                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">{profile.specialty || (lang === 'fr' ? "Style libre" : "Free style")}</span>
                                    <button onClick={() => setIsEditing(true)} className="text-[9px] font-black text-slate-500 uppercase tracking-widest hover:text-white transition-colors">Modifier Fiche</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="space-y-8">
                <div className="flex justify-between items-end px-4">
                    <div>
                        <h4 className="text-xl font-black text-white uppercase italic tracking-tighter">Vidéos de Cours</h4>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.4em] mt-1">Stockage local sur votre ordinateur</p>
                    </div>
                    <button 
                        onClick={() => setIsAdding(true)}
                        className="px-8 py-3 bg-white text-black rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-emerald-500 hover:text-white transition-all shadow-xl"
                    >
                        + Ajouter Vidéo Prof
                    </button>
                </div>

                {isAdding && (
                    <div className="fixed inset-0 z-[20000] flex items-center justify-center p-6 bg-black/90 backdrop-blur-3xl animate-fade-in">
                        <form onSubmit={handleAddVideo} className="bg-slate-900 p-10 rounded-[50px] border border-white/10 w-full max-w-xl space-y-6 shadow-[0_0_100px_rgba(0,0,0,1)]">
                            <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">Nouvel Exercice Prof</h3>
                            <div className="space-y-4">
                                <input name="title" required placeholder="Titre de l'exercice (ex: Gamme de Mi mineur)" className="w-full bg-black/60 p-4 rounded-2xl border border-white/10 text-white outline-none focus:border-emerald-500" />
                                <div className="grid grid-cols-2 gap-4">
                                    <input name="bpm" type="number" placeholder="BPM" className="w-full bg-black/60 p-4 rounded-2xl border border-white/10 text-white outline-none focus:border-emerald-500" />
                                    <input name="grid" placeholder="Grille (ex: Em | D | C | B7)" className="w-full bg-black/60 p-4 rounded-2xl border border-white/10 text-white outline-none focus:border-emerald-500" />
                                </div>
                                <textarea name="notes" placeholder="Notes pédagogiques / conseils du prof..." className="w-full bg-black/60 p-4 rounded-2xl border border-white/10 text-white h-32 resize-none outline-none focus:border-emerald-500" />
                                <div className="p-8 border-2 border-dashed border-white/10 rounded-3xl bg-black/20 text-center space-y-4">
                                    <input type="file" ref={fileInputRef} accept="video/*" required className="hidden" id="prof-vid-upload" />
                                    <label htmlFor="prof-vid-upload" className="cursor-pointer flex flex-col items-center gap-4">
                                        <span className="text-4xl">📹</span>
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Choisir la vidéo du cours</span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <button type="button" onClick={() => setIsAdding(false)} className="flex-1 py-4 rounded-2xl bg-white/5 text-slate-400 font-black uppercase text-[10px]">Annuler</button>
                                <button type="submit" className="flex-[2] py-4 rounded-2xl bg-emerald-500 text-black font-black uppercase text-[10px] shadow-xl">Sauvegarder en local</button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {videos.length === 0 ? (
                        <div className="col-span-full py-20 text-center border-2 border-dashed border-white/5 rounded-[60px] opacity-30">
                            <p className="text-xs font-black text-slate-500 uppercase tracking-[0.5em]">Aucune vidéo prof enregistrée</p>
                        </div>
                    ) : videos.map(video => (
                        <div key={video.id} className="bg-white/5 border border-white/10 rounded-[48px] p-8 flex flex-col gap-6 hover:bg-white/[0.07] transition-all group relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/40"></div>
                            
                            <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                    <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">{new Date(video.date).toLocaleDateString()}</span>
                                    <h5 className="text-2xl font-black text-white italic uppercase tracking-tighter">{video.title}</h5>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleExportGsc(video)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all text-emerald-400 hover:bg-emerald-500/20" title="Exporter en .gsc">📦</button>
                                    <button onClick={() => handleDelete(video.id)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all text-red-400 hover:bg-red-500/20">✕</button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-black/40 p-4 rounded-2xl border border-white/5 text-center">
                                    <span className="text-[8px] font-black text-slate-500 uppercase block mb-1">Tempo</span>
                                    <span className="text-lg font-black text-smart-gold italic">{video.bpm} BPM</span>
                                </div>
                                <div className="bg-black/40 p-4 rounded-2xl border border-white/5 text-center">
                                    <span className="text-[8px] font-black text-slate-500 uppercase block mb-1">Structure</span>
                                    <span className="text-sm font-black text-white italic truncate block">{video.grid || 'Standard'}</span>
                                </div>
                            </div>

                            {video.notes && (
                                <p className="text-xs text-slate-400 italic leading-relaxed line-clamp-2 px-2">"{video.notes}"</p>
                            )}

                            <button 
                                onClick={() => onSelectVideo(video)}
                                className="w-full py-5 bg-white text-black rounded-[24px] font-black uppercase text-[11px] tracking-[0.2em] shadow-xl hover:bg-emerald-500 hover:text-white hover:scale-[1.02] transition-all flex items-center justify-center gap-3"
                            >
                                <span>LANCER L'ANALYSE ⚔️</span>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
